import {
  VAULT_DEFAULT_DOC_TYPE,
  VAULT_DEFAULT_STATUS,
  VAULT_DEFAULT_VISIBILITY,
  VAULT_DOC_TYPES,
  VAULT_LINK_ENTITY_TYPES,
  VAULT_STATUSES,
  VAULT_VISIBILITIES,
  type VaultDocType,
  type VaultDocumentDetail,
  type VaultDocumentUpsertRequest,
  type VaultLinkEntityType,
  type VaultStatus,
  type VaultVisibility,
} from '@oneup/shared';
import {
  proposalsApi,
  tasksApi,
  vaultApi,
  type CopilotMessage,
  type CopilotPendingConfirmation,
} from '@/lib/api';

export interface AppliedCopilotApproval {
  toolResult: CopilotMessage;
  notice: string;
  navigateTo?: string;
  invalidate: Array<readonly unknown[]>;
}

const FIELD_LABELS: Record<string, string> = {
  name: 'Name',
  status: 'Status',
  priority: 'Priority',
  notes: 'Notes',
  targetDate: 'Target date',
  startDate: 'Start date',
  billStatus: 'Bill status',
  finalReview: 'Final review',
  estimateMinutes: 'Estimate (min)',
  nextAction: 'Next action',
  definitionOfDone: 'Definition of done',
  questionForClients: 'Question for client',
  proposalName: 'Proposal name',
  proposalType: 'Proposal type',
  portalVisibility: 'Portal visibility',
  proposalAmount: 'Proposal amount',
  depositAmount: 'Deposit amount',
  proposalDate: 'Proposal date',
  validUntil: 'Valid until',
  scopeSummary: 'Scope summary',
  deliverables: 'Deliverables',
  timeline: 'Timeline',
  paymentTerms: 'Payment terms',
  clientFacingNextStep: 'Client-facing next step',
  internalNextAction: 'Internal next action',
  generatedDocUrl: 'Generated doc URL',
  slug: 'Slug',
  title: 'Title',
  doc_type: 'Document type',
  visibility: 'Visibility',
  folder_path: 'Folder',
  tags: 'Tags',
  content_md: 'Markdown',
  summary: 'Summary',
  change_note: 'Change note',
  document_id: 'Document ID',
  entity_type: 'Entity type',
  entity_id: 'Entity ID',
  relation_type: 'Relation',
};

export async function applyCopilotApproval(
  pending: CopilotPendingConfirmation,
): Promise<AppliedCopilotApproval> {
  if (pending.toolName === 'openProposal') return applyOpenProposal(pending);
  if (pending.toolName === 'createProposalDraft') return applyCreateProposal(pending);
  if (pending.toolName === 'updateProposalFields') return applyUpdateProposal(pending);
  if (pending.toolName === 'create_task') return applyCreateTask(pending);
  if (pending.toolName === 'update_task') return applyUpdateTask(pending);
  if (
    pending.toolName === 'alice_vault_propose_document_update' ||
    pending.toolName === 'alice_vault_propose_new_document'
  ) {
    return applyVaultUpsert(pending);
  }
  if (pending.toolName === 'alice_vault_propose_document_link') {
    return applyVaultLink(pending);
  }
  throw new Error(`Unsupported tool: ${pending.toolName}`);
}

export function getApprovalTitle(pending: CopilotPendingConfirmation): string {
  switch (pending.toolName) {
    case 'create_task':
      return `Create task: ${stringArg(pending.args.name) ?? 'Untitled task'}`;
    case 'update_task':
      return `Update task ${stringArg(pending.args.id) ?? ''}`.trim();
    case 'openProposal':
      return `Open proposal ${stringArg(pending.args.proposalId) ?? ''}`.trim();
    case 'createProposalDraft': {
      const input = objectArg(pending.args.input);
      return `Create proposal: ${stringArg(input?.proposalName) ?? 'Untitled proposal'}`;
    }
    case 'updateProposalFields':
      return `Update proposal ${stringArg(pending.args.proposalId) ?? ''}`.trim();
    case 'alice_vault_propose_document_update':
      return `Update Vault doc: ${stringArg(pending.args.slug) ?? 'unknown'}`;
    case 'alice_vault_propose_new_document':
      return `Create Vault doc: ${stringArg(pending.args.title) ?? stringArg(pending.args.slug) ?? 'Untitled'}`;
    case 'alice_vault_propose_document_link':
      return `Link Vault doc ${stringArg(pending.args.document_id) ?? 'unknown'}`;
    default:
      return pending.summary || pending.toolName;
  }
}

export function getApprovalPatch(pending: CopilotPendingConfirmation): Record<string, unknown> {
  const args = pending.args ?? {};
  const patch = objectArg(args.patch);
  if (patch) return patch;
  const fields = objectArg(args.fields);
  if (fields) return fields;
  const input = objectArg(args.input);
  if (input) return input;

  const flat: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(args)) {
    if (
      key === 'id' ||
      key === 'taskId' ||
      key === 'proposalId' ||
      key === 'rationale' ||
      key === 'tenant_id' ||
      key === 'create_version'
    ) {
      continue;
    }
    flat[key] = value;
  }
  return flat;
}

export function getApprovalFieldLabel(key: string): string {
  return FIELD_LABELS[key] ?? key;
}

export function formatApprovalValue(value: unknown): string {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

export function buildApprovalFeedbackPrompt(
  pending: CopilotPendingConfirmation,
  feedback: string,
): string {
  return [
    'Regenerate this pending approval instead of applying the current version.',
    `Tool: ${pending.toolName}`,
    `Title: ${getApprovalTitle(pending)}`,
    pending.summary ? `Current summary:\n${pending.summary}` : null,
    `Current payload:\n${JSON.stringify(pending.args, null, 2)}`,
    `Feedback:\n${feedback.trim()}`,
    'Return a new approval card with the revised action. Do not claim anything was written yet.',
  ]
    .filter((line): line is string => Boolean(line))
    .join('\n\n');
}

export function approvalRationale(pending: CopilotPendingConfirmation): string | undefined {
  if (typeof pending.rationale === 'string' && pending.rationale.trim()) return pending.rationale;
  const rationale = stringArg(pending.args.rationale) ?? stringArg(pending.args.change_note);
  return rationale || undefined;
}

function applyOpenProposal(pending: CopilotPendingConfirmation): AppliedCopilotApproval {
  const proposalId = requireString(pending.args.proposalId, 'proposalId');
  return {
    toolResult: toolResult(pending, { ok: true, proposalId, opened: true }),
    notice: `Opened proposal ${proposalId}`,
    navigateTo: `/proposals/${proposalId}`,
    invalidate: [],
  };
}

async function applyCreateProposal(pending: CopilotPendingConfirmation): Promise<AppliedCopilotApproval> {
  const input = objectArg(pending.args.input);
  if (!input) throw new Error('Pending proposal create is missing input.');
  const created = await proposalsApi.create(input as Parameters<typeof proposalsApi.create>[0]);
  return {
    toolResult: toolResult(pending, {
      ok: true,
      proposalId: created.id,
      proposalName: created.proposalName,
    }),
    notice: `Created proposal draft - ${created.proposalName}`,
    navigateTo: `/proposals/${created.id}`,
    invalidate: [['proposals']],
  };
}

async function applyUpdateProposal(pending: CopilotPendingConfirmation): Promise<AppliedCopilotApproval> {
  const proposalId = requireString(pending.args.proposalId, 'proposalId');
  const fields = objectArg(pending.args.fields);
  if (!fields) throw new Error('Pending update is missing fields.');
  const updated = await proposalsApi.update(proposalId, fields as Record<string, unknown>);
  return {
    toolResult: toolResult(pending, {
      ok: true,
      proposalId: updated.id,
      patched: pickAppliedFields(fields, updated as unknown as Record<string, unknown>),
    }),
    notice: `Updated proposal - ${updated.proposalName}`,
    invalidate: [['proposals'], ['proposal', proposalId]],
  };
}

async function applyCreateTask(pending: CopilotPendingConfirmation): Promise<AppliedCopilotApproval> {
  const name = requireString(pending.args.name, 'name');
  const created = await tasksApi.create({
    name,
    notes: stringArg(pending.args.notes),
    status: stringArg(pending.args.status),
    priority: stringArg(pending.args.priority),
    clientId: stringArg(pending.args.clientId),
    websiteId: stringArg(pending.args.websiteId),
    dueDate: stringArg(pending.args.targetDate) ?? stringArg(pending.args.dueDate),
  });
  return {
    toolResult: toolResult(pending, { ok: true, taskId: created.id, taskName: created.name }),
    notice: `Created task - ${created.name ?? name}`,
    navigateTo: `/tasks/${created.id}`,
    invalidate: [['tasks']],
  };
}

async function applyUpdateTask(pending: CopilotPendingConfirmation): Promise<AppliedCopilotApproval> {
  const id = requireString(pending.args.id, 'id');
  const patch = objectArg(pending.args.patch);
  if (!patch) throw new Error('Pending update is missing a patch payload.');
  const updated = await tasksApi.patch(id, patch as Record<string, unknown>);
  return {
    toolResult: toolResult(pending, {
      ok: true,
      taskId: updated.id,
      patched: pickAppliedFields(patch, updated as unknown as Record<string, unknown>),
      client: updated.client ? { id: updated.client.id, name: updated.client.name } : null,
      website: updated.website
        ? {
            id: updated.website.id,
            name: updated.website.friendlyName ?? updated.website.url,
          }
        : null,
    }),
    notice: `Updated task - ${updated.name ?? id}`,
    invalidate: [['tasks'], ['task', id]],
  };
}

async function applyVaultUpsert(pending: CopilotPendingConfirmation): Promise<AppliedCopilotApproval> {
  const payload = await normalizeVaultUpsertPayload(pending);
  const result = await vaultApi.upsert(payload);
  return {
    toolResult: toolResult(pending, {
      ok: true,
      action: 'vault.document.upsert',
      documentId: result.document_id,
      slug: result.slug,
      version: result.version,
      updatedAt: result.updated_at,
    }),
    notice: `Updated Vault doc - ${result.slug}`,
    navigateTo: `/knowledge/md-vault?doc=${encodeURIComponent(result.document_id)}`,
    invalidate: [
      ['vault', 'list'],
      ['vault', 'doc', result.document_id],
      ['vault', 'versions', result.document_id],
    ],
  };
}

async function applyVaultLink(pending: CopilotPendingConfirmation): Promise<AppliedCopilotApproval> {
  const documentId = requireString(pending.args.document_id, 'document_id');
  const rawEntityType = requireString(pending.args.entity_type, 'entity_type');
  const entityId = requireString(pending.args.entity_id, 'entity_id');
  const entityType = normalizeVaultEntityType(rawEntityType);
  const clientId = stringArg(pending.args.client_id) ?? (rawEntityType === 'client' ? entityId : undefined);
  const link = await vaultApi.addLink(documentId, {
    entityType,
    entityId,
    relationType: stringArg(pending.args.relation_type),
    clientId,
  });
  return {
    toolResult: toolResult(pending, {
      ok: true,
      action: 'vault.document.link',
      documentId,
      linkId: link.id,
      entityType,
      entityId,
    }),
    notice: 'Linked Vault document',
    navigateTo: `/knowledge/md-vault?doc=${encodeURIComponent(documentId)}`,
    invalidate: [
      ['vault', 'list'],
      ['vault', 'doc', documentId],
    ],
  };
}

async function normalizeVaultUpsertPayload(
  pending: CopilotPendingConfirmation,
): Promise<VaultDocumentUpsertRequest> {
  const slugOrId = requireString(pending.args.slug, 'slug');
  const contentMd = requireRawString(pending.args.content_md, 'content_md');
  const tenantId = stringArg(pending.args.tenant_id) ?? 'one-up';
  const existing = await maybeGetVaultDocument(slugOrId);
  const existingDoc = existing?.document;
  const slug = existingDoc?.slug ?? slugOrId;

  return {
    tenant_id: tenantId,
    slug,
    title: stringArg(pending.args.title) ?? existingDoc?.title ?? titleFromSlug(slug),
    doc_type: enumArg(pending.args.doc_type, VAULT_DOC_TYPES) ?? existingDoc?.docType ?? VAULT_DEFAULT_DOC_TYPE,
    status: enumArg(pending.args.status, VAULT_STATUSES) ?? existingDoc?.status ?? VAULT_DEFAULT_STATUS,
    visibility:
      enumArg(pending.args.visibility, VAULT_VISIBILITIES) ??
      existingDoc?.visibility ??
      VAULT_DEFAULT_VISIBILITY,
    folder_path:
      pending.args.folder_path !== undefined
        ? stringArg(pending.args.folder_path) ?? null
        : existingDoc?.folderPath ?? null,
    tags: Array.isArray(pending.args.tags)
      ? pending.args.tags.filter((item): item is string => typeof item === 'string').map((item) => item.trim()).filter(Boolean)
      : existingDoc?.tags ?? [],
    content_md: contentMd,
    summary:
      pending.args.summary !== undefined
        ? typeof pending.args.summary === 'string'
          ? pending.args.summary
          : null
        : existingDoc?.summary ?? null,
    ...(stringArg(pending.args.client_id) ? { client_id: stringArg(pending.args.client_id) } : {}),
    ...(typeof pending.args.create_version === 'boolean'
      ? { create_version: pending.args.create_version }
      : {}),
  };
}

async function maybeGetVaultDocument(ref: string): Promise<VaultDocumentDetail | null> {
  try {
    return await vaultApi.get(ref);
  } catch {
    return null;
  }
}

function normalizeVaultEntityType(value: string): VaultLinkEntityType {
  if (value === 'client') return 'organization';
  if ((VAULT_LINK_ENTITY_TYPES as readonly string[]).includes(value)) return value as VaultLinkEntityType;
  throw new Error(`Unsupported Vault link entity_type: ${value}`);
}

function toolResult(pending: CopilotPendingConfirmation, content: Record<string, unknown>): CopilotMessage {
  return {
    role: 'tool',
    tool_call_id: pending.toolCallId,
    content: JSON.stringify(content),
  };
}

function pickAppliedFields(
  patch: Record<string, unknown>,
  updated: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(patch)) {
    if (key in updated) out[key] = updated[key];
  }
  return out;
}

function objectArg(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

function stringArg(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function requireString(value: unknown, field: string): string {
  const str = stringArg(value);
  if (!str) throw new Error(`Pending approval is missing ${field}.`);
  return str;
}

function requireRawString(value: unknown, field: string): string {
  if (typeof value !== 'string') throw new Error(`Pending approval is missing ${field}.`);
  return value;
}

function enumArg<T extends string>(value: unknown, allowed: readonly T[]): T | undefined {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : undefined;
}

function titleFromSlug(slug: string): string {
  return slug
    .split(/[/-]/g)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export type { VaultDocType, VaultStatus, VaultVisibility };
